import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Space,
  Modal,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { Filtros } from "./filtros";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import "dayjs/locale/es";
import { finanzasEmpresaApi } from "../../services/finanzasEmpresa.api";
import { formatPropertyName } from "../../utils/propertyDisplay";
import {
  CarruselAdjuntos,
  MiniaturaAdjunto,
  adjuntosDeRegistro,
  type Adjunto,
} from "./adjuntos";
import { FiltroFecha } from "./filtroGlobalFecha";
import { exportToXlsxSingleSheet, fetchAllPages } from "./exportXls";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
// Round 2026-05-01 — sweep general post-fix /sueldos.
import { getMsg } from "../../utils/antdStatic";
import { ahoraChile } from "../../utils/fechaChile";

import { paginacionCliente } from "../../utils/tablaPaginacion";
// Establece el idioma global en dayjs (si es necesario)
dayjs.locale("es");

export interface ProductoRegistro {
  id: string;
  tipo?: "compra" | "movimiento";
  ubicacion: string;
  ubicacionTipo?: string;
  ubicacionId?: number;
  nombre: string;
  marca?: string | null;
  cantidadUnidad?: number | null;
  cantidadPack?: number | null;
  precioUnidad?: number | null;
  precioPack?: number | null;
  montoTotal?: number | null;
  fecha?: string | null;
  /** URLs completas (compat). Para pintar usa `imagenesDetalle`. */
  imagenes?: string[];
  /** Adjuntos con miniatura, versión liviana del video y tipo (backend: utils/archivosDetalle.js). */
  imagenesDetalle?: Array<{
    url: string;
    thumbUrl?: string | null;
    previewUrl?: string | null;
    kind?: string;
    originalName?: string | null;
  }>;
  origenTipo?: string | null;
  origenId?: number | null;
  origenNombre?: string | null;
}

export interface OpcionBasica {
  id: number;
  nombre: string;
}

export function ProductosSection({
  marcas,
  bodegas,
  bodeguitas,
  propiedades,
  filtroFecha,
  refreshKey,
}: {
  data: ProductoRegistro[];
  loading?: boolean;
  marcas: OpcionBasica[];
  bodegas: OpcionBasica[];
  bodeguitas: OpcionBasica[];
  propiedades: OpcionBasica[];
  filtroFecha?: FiltroFecha;
  /** Contador de refresco manual (botón "Actualizar" del header). */
  refreshKey?: number;
}) {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  // El getter no se usa en UI; setTotal solo alimenta el paginador.
  const [, setTotal] = useState(0);
  // Imagen modal state
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const [images, setImages] = useState<Adjunto[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  const [jsonOpen, setJsonOpen] = useState(false);
  const [jsonTarget, setJsonTarget] = useState<any>(null);

  // Round 2026-05-08: ahora exponemos `filters` (antes solo el setter)
  // porque el botón "Exportar a XLS" necesita re-fetchar todas las
  // páginas con los mismos filtros aplicados.
  // Round 2026-09-24: además se recuerdan por usuario. Esta sección solo vive
  // en /finanzas-empresa, así que la clave va fija acá.
  const [filters, setFilters] = useFiltroPersistido<Record<string, any>>(
    "finanzasEmpresa.productos.filtros",
    {},
    { validar: (v) => !!v && typeof v === "object" && !Array.isArray(v) },
  );

  const currencyCLP = (v?: number | null) => {
    const n = Number(v);
    if (v == null || Number.isNaN(n)) return "—";
    return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP" }).format(n);
  };
  const bodegasMap = useMemo(
    () => new Map(bodegas.map((u) => [u.id, u.nombre])),
    [bodegas],
  );
  const bodeguitasMap = useMemo(
    () => new Map(bodeguitas.map((u) => [u.id, u.nombre])),
    [bodeguitas],
  );
  const propiedadesMap = useMemo(
    () => new Map(propiedades.map((u) => [u.id, u.nombre])),
    [propiedades],
  );

  const columns: ColumnsType<ProductoRegistro> = [
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
      width: 110,
      align: "center",
      render: (v: string) => (
        <Tag color={v === "compra" ? "green" : "blue"}>
          {v === "compra" ? "Compra" : "Movimiento"}
        </Tag>
      ),
    },
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      width: 200,
      render: (v) => v,
    },
    {
      title: "Destino",
      key: "ubicacion",
      width: 200,
      render: (_, r) => {
        // Solo el nombre de PROPIEDAD se muestra en mayúsculas; bodega y
        // bodeguita son otras entidades y se dejan tal cual.
        const name =
          r.ubicacionTipo === "propiedades"
            ? formatPropertyName(propiedadesMap.get(r.ubicacionId!))
            : r.ubicacionTipo === "bodegas"
              ? bodegasMap.get(r.ubicacionId!)
              : r.ubicacionTipo === "bodeguitas"
                ? bodeguitasMap.get(r.ubicacionId!)
                : null;
        return name || r.ubicacion || "—";
      },
    },
    {
      title: "Origen",
      key: "origen",
      width: 160,
      render: (_, r) => {
        if (r.origenNombre) return r.origenNombre;
        if (!r.origenTipo || !r.origenId) return "—";
        // Solo PROPIEDAD en mayúsculas; bodega/bodeguita quedan tal cual.
        if (r.origenTipo === "propiedades") return formatPropertyName(propiedadesMap.get(r.origenId)) || "Propiedad";
        if (r.origenTipo === "bodegas") return bodegasMap.get(r.origenId) ?? "Bodega";
        if (r.origenTipo === "bodeguitas") return bodeguitasMap.get(r.origenId) ?? "Bodeguita";
        return r.origenTipo;
      },
    },
    {
      title: "Cantidades (unidad/pack)",
      key: "cantidades",
      width: 180,
      align: "center",
      render: (_, r) => (
        <span>
          {r.cantidadUnidad ?? "—"} / {r.cantidadPack ?? "—"}
        </span>
      ),
    },
    { title: "Marca", dataIndex: "marca", key: "marca", width: 120 },
    {
      title: "Monto total",
      dataIndex: "montoTotal",
      key: "montoTotal",
      width: 140,
      align: "right",
      render: (v: number) =>
        v != null ? currencyCLP(v) : "—",
    },
    {
      title: "Precios (unidad/pack)",
      key: "precios",
      width: 200,
      align: "center",
      render: (_, r) => (
        <Space size={4}>
          <Tag>{r.precioUnidad != null ? currencyCLP(r.precioUnidad) : "—"}</Tag>
          <span>/</span>
          <Tag>{r.precioPack != null ? currencyCLP(r.precioPack) : "—"}</Tag>
        </Space>
      ),
    },
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      width: 120,
      align: "center",
      render: (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "—"),
    },
    {
      title: "Imágenes/Videos",
      dataIndex: "imagenes",
      key: "imagenes",
      width: 160,
      align: "center",
      // La celda pinta la MINIATURA (la foto completa pesa varios MB y acá cabe en 36 px); el
      // carrusel de abajo abre el archivo completo, y el video con su versión liviana. El tipo
      // sale del archivo (`kind`), no de la extensión: las URLs firmadas no la llevan.
      render: (_: unknown, r) => {
        const adjuntos = adjuntosDeRegistro(r.imagenes, r.imagenesDetalle);
        if (!adjuntos.length) return "—";
        const abrir = () => {
          setImages(adjuntos);
          setIsImageModalVisible(true);
          setCurrentSlide(0);
        };
        return (
          <div className="thumbs">
            {adjuntos.slice(0, 3).map((a, i) => (
              <MiniaturaAdjunto
                key={`${a.url}-${i}`}
                adjunto={a}
                size={36}
                onClick={abrir}
                testId="producto-miniatura"
              />
            ))}
            {adjuntos.length > 3 ? <Tag>+{adjuntos.length - 3}</Tag> : null}
          </div>
        );
      },
    },
    {
      title: "Detalles",
      key: "detalles",
      width: 120,
      align: "center",
      render: (_, r) => (
        <Button
          size="small"
          onClick={() => {
            setJsonTarget(r);
            setJsonOpen(true);
          }}
        >
          Ver JSON
        </Button>
      ),
    },
  ];

  // El endpoint consulta InventoryTransaction — las columnas reales son
  // producto_nombre / destino_tipo / destino_id / cantidad_unidades /
  // cantidad_packs / precio_por_*. La versión anterior de este mapUiToApi
  // estaba escrita para el modelo Inventory (nombre / marca / ubicacion_*):
  // buildWhereFromFields descartaba todos esos campos por su allow-set y
  // NINGÚN filtro de esta pestaña filtraba nada (auditoría 2026-07-13).
  const mapUiToApi = (uiFilters: Record<string, any>, page: number) => {
    const fields: any = {};

    // Texto libre → nombre del producto (incluye color/forma, porque
    // producto_nombre se compone con esos atributos).
    if (uiFilters.nombre) {
      fields.producto_nombre = { contains: uiFilters.nombre };
    }

    // Marca: no es columna de la transacción (viene del include
    // `inventario`); va como campo virtual y el backend la resuelve
    // filtrando el include.
    if (uiFilters.marca) {
      fields._marca = uiFilters.marca;
    }

    // Rangos numéricos con el contrato del backend ({ eq: {min,max} }).
    const numRange = (keyUi: string, fieldDb: string) => {
      const v = uiFilters[keyUi];
      if (v && (v.min != null || v.max != null)) {
        fields[fieldDb] = { eq: v };
      }
    };

    numRange("cantidadUnidad", "cantidad_unidades");
    numRange("cantidadPack", "cantidad_packs");
    numRange("precioUnidad", "precio_por_unidad");
    numRange("precioPack", "precio_por_pack");

    if (uiFilters.id != null) {
      fields.id = { eq: uiFilters.id };
    }

    // Destino del movimiento (propiedad / bodega / bodeguita).
    if (uiFilters.propiedadId != null) {
      fields.destino_tipo = { eq: "propiedades" };
      fields.destino_id = { eq: uiFilters.propiedadId };
    } else if (uiFilters.bodegaId != null) {
      fields.destino_tipo = { eq: "bodegas" };
      fields.destino_id = { eq: uiFilters.bodegaId };
    } else if (uiFilters.bodeguitaId != null) {
      fields.destino_tipo = { eq: "bodeguitas" };
      fields.destino_id = { eq: uiFilters.bodeguitaId };
    }

    return { filters: { fields }, page, page_size: 20 };
  };

  const buildGlobalRange = () => {
    const ff = filtroFecha ?? { modo: "mes", mes: ahoraChile() };
    if (ff.modo === "mes" || ff.modo === "anterior") {
      const m = ff.mes ?? ahoraChile();
      return { from: m.startOf("month"), to: m.endOf("month") };
    }
    if (ff.modo === "rango" && ff.rango) {
      return { from: ff.rango[0].startOf("day"), to: ff.rango[1].endOf("day") };
    }
    const m = ahoraChile();
    return { from: m.startOf("month"), to: m.endOf("month") };
  };

  const fetchProducts = useCallback(
    async (uiFilters: Record<string, any> = {}, newPage = 1) => {
      try {
        setLoading(true);
        const globalRange = buildGlobalRange();
        const body = {
          ...mapUiToApi(uiFilters, newPage),
          globalDateRange: [
            globalRange.from.format("YYYY-MM-DD"),
            globalRange.to.format("YYYY-MM-DD"),
          ],
          page: newPage,
        };
        const json = await finanzasEmpresaApi.searchProductos(body);
        if (!json) return;
        const dataMapped = json.data.map((item: any) => ({
          ...item,
          ubicacion:
            item.ubicacionTipo === "propiedades"
              ? propiedadesMap.get(item.ubicacionId)
              : item.ubicacionTipo === "bodegas"
                ? bodegasMap.get(item.ubicacionId)
                : item.ubicacionTipo === "bodeguitas"
                  ? bodeguitasMap.get(item.ubicacionId)
                  : "Desconocida",
        }));
        setData(dataMapped);
        setTotal(json.total);
      } catch (err: any) {
        getMsg()?.error(err.message || "Error al cargar arriendos");
      } finally {
        setLoading(false);
      }
    },
    [page, propiedadesMap, bodegasMap, bodeguitasMap, filtroFecha],
  );

  // Las consultas salen con los filtros vigentes (antes iban con `{}`): con el
  // filtro avanzado recordado, pedir sin filtrar dejaría el panel mostrando un
  // filtro aplicado y la tabla trayendo todo el período.
  useEffect(() => {
    fetchProducts(filters, 1);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroFecha, refreshKey]);

  useEffect(() => {
    fetchProducts(filters, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="finanzas-section">
      <div className="toolbar">
        <Filtros
          persistenceKey={"productos"}
          // Sin `value` el filtro recordado quedaría invisible: la tabla sale
          // recortada y el panel, vacío.
          value={filters}
          items={[
            // "Producto" pasó de Select a texto libre (auditoría 2026-07-13):
            // el catálogo `nombres` nunca se cargaba (Select sin opciones) y
            // el value era el ID del catálogo, inservible para un contains.
            {
              key: "nombre",
              type: "text",
              label: "Producto",
              placeholder: "nombre, color, forma…",
            },
            {
              key: "propiedadId",
              type: "select",
              label: "Propiedad",
              allowSearch: true,
              options: propiedades.map((u) => ({
                value: u.id,
                label: formatPropertyName(u.nombre),
              })),
            },
            {
              key: "bodegaId",
              type: "select",
              label: "Bodegas",
              allowSearch: true,
              options: bodegas.map((p) => ({
                value: p.id,
                label: p.nombre,
              })),
            },
            {
              key: "bodeguitaId",
              type: "select",
              label: "Bodeguitas",
              allowSearch: true,
              options: bodeguitas.map((p) => ({
                value: p.id,
                label: p.nombre,
              })),
            },
            {
              key: "marca",
              type: "select",
              label: "Marca",
              allowSearch: true,
              // value = el NOMBRE de la marca (no el id del catálogo): el
              // backend filtra el include `inventario.marca` por texto.
              options: marcas.map((u) => ({
                value: u.nombre,
                label: u.nombre,
              })),
            },
            // El input "Buscar" (searchQuery) se retiró: nunca se mapeaba al
            // request (filtro muerto) y su función la cubre "Producto".
          ]}
          onApply={(appliedFilters) => {
            setFilters(appliedFilters);
            setPage(1);
            fetchProducts(appliedFilters, 1);
          }}
        />
        <Button
          onClick={async () => {
            // Round 2026-05-08: exportar todas las páginas. Ver
            // sectionArriendos.
            const hide = getMsg()?.loading("Preparando exportación...", 0);
            try {
              const globalRange = buildGlobalRange();
              const allRows = await fetchAllPages<any>(async (page, pageSize) => {
                const body = {
                  ...mapUiToApi(filters, page),
                  globalDateRange: [
                    globalRange.from.format("YYYY-MM-DD"),
                    globalRange.to.format("YYYY-MM-DD"),
                  ],
                  page,
                  page_size: pageSize,
                };
                const json = await finanzasEmpresaApi.searchProductos(body);
                if (!json) return null;
                return { data: json.data, total: json.total };
              });
              const now = ahoraChile().format("YYYY-MM-DD_HH-mm");
              await exportToXlsxSingleSheet({
                filename: `Productos_${now}.xlsx`,
                sheetName: "Productos",
                rows: allRows,
              });
              getMsg()?.success(`Exportadas ${allRows.length} filas`);
            } catch (err: any) {
              getMsg()?.error(err?.message || "Error al exportar");
            } finally {
              hide?.();
            }
          }}
        >
          Exportar a XLS
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={paginacionCliente({ porDefecto: 10, etiqueta: "producto" })}
        scroll={{ x: 1200 }}
      />

      {/* Modal de imágenes (carrusel) */}
      <Modal
        open={isImageModalVisible}
        title="Imágenes"
        onCancel={() => {
          setIsImageModalVisible(false);
          setImages([]);
        }}
        width={960}
        styles={{
          body: { paddingBottom: 56, maxHeight: "80vh", overflow: "hidden" },
        }}
      >
        <CarruselAdjuntos
          adjuntos={images}
          actual={currentSlide}
          onCambio={setCurrentSlide}
          vacio="No hay imágenes para este producto."
        />
      </Modal>

      {/* Modal JSON Detalle */}
      <Modal
        open={jsonOpen}
        title="Detalle del producto (JSON)"
        onCancel={() => {
          setJsonOpen(false);
          setJsonTarget(null);
        }}
        footer={null}
        width={720}
      >
        <pre
          style={{
            maxHeight: "70vh",
            overflow: "auto",
            background: "#0f172a",
            color: "#e2e8f0",
            padding: 16,
            borderRadius: 8,
          }}
        >
          {JSON.stringify(jsonTarget, null, 2)}
        </pre>
      </Modal>
    </div>
  );
}
